import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Input,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import Select from "react-select";
import { LabelWithToolTip } from "../../atoms/LabelWithToolTip";
import { isNullOrEmpty } from "../../../utils/constants";
import { apiGetRequest, apiUpdateRequest } from "../../../utils/queries";
import { Validate } from "../../../utils/Validator";
import { trackPromise } from "react-promise-tracker";
import Alerts from "../../molecules/Alerts";
import { v4 as uuidv4 } from "uuid";
import "./AccountInformationPage.css";
import { useModalCleanup } from "../../../utils/useModalCleanup";

const AccountInformationPage = () => {
  const navigate = useNavigate();
  const { token } = useParams();
    const modalCleanup = useModalCleanup();

  const [account, setAccount] = useState({});
  const [accountTypes, setAccountTypes] = useState();
  const [countryList, setCountryList] = useState([]);
  const [paymentTermList, setPaymentTermList] = useState([]);
  const [accountLoaded, setAccountLoaded] = useState(false);
  const [accountHasChanged, setAccountHasChanged] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);
  const [alerts, setAlerts] = useState([]);


  const { register, handleSubmit, formState: {errors}, getValues, watch, reset, setValue } = useForm({
    defaultValues: {
      isIBAN: true,
    },
  });

  // Watch for form changes to set accountHasChanged
  const watchedValues = watch();
  
  useEffect(() => {
    if (accountLoaded) {
      setAccountHasChanged(true);
    }
  }, [watchedValues, accountLoaded]);

  /**
   * Load account data when component mounts or token changes.
   * Fetches account details, country list, and payment terms.
   */
  useEffect(() => {
    const getRequest = async () => {
      try {
        const request = await trackPromise(apiGetRequest(token));
        setAccount(request.data.request);
        setAccountTypes(request.data.accountTypes);
        setCountryList(request.data.countryList);
        setPaymentTermList(request.data.paymentTermList);
        setAccountLoaded(true);
      } catch (error) {
        addAlert("Error loading account data. Please try again. " + error, "error");
      }
    };

    if (token) {
      getRequest();
    }
  }, [token]);

  /**
   * Update form values when account data is loaded.
   * Sets default values for the form fields based on the account data.
   */
  useEffect(() => {
    if (accountLoaded && paymentTermList.length > 0 && !formInitialized) {
      // Pool Partner hides Payment Term, so it must stay empty instead of defaulting.
      const paymentTermId = hasPoolPartner()
        ? ""
        : paymentTermList.find(
            (term) =>
              term.displayName === account.paymentTerm ||
              term.id === account.paymentTerm
          )?.id ?? paymentTermList.find((x) => x.isDefault === 1)?.id ?? "";

      const formValues = {
        isIBAN: true,
        name: account.name || "",
        address1: account.address1 || "",
        address2: account.address2 || "",
        city: account.city || "",
        zip: account.zip || "",
        countryId: account.countryId || "",
        phone: account.phone || "",
        salesEmail: account.salesEmail || "",
        financeEmail: account.financeEmail || "",
        taxRegistrationNumber: account.taxRegistrationNumber || "",
        paymentTerm: paymentTermId,
        salesContactName: account.salesContactName || "",
        salesContactMobile: account.salesContactMobile || "",
        notes: account.notes || ""
      };

      // Reset the form with all account data at once - only on first load
      reset(formValues);
      
      setFormInitialized(true);
    }
  }, [accountLoaded, paymentTermList.length, formInitialized, account, reset]);

  const addAlert = (message, color, timeout = 7000) => {
    let id = uuidv4();
    setAlerts((prev) => [
      ...prev,
      { message: message, id: id, color: color, timeout: timeout },
    ]);
  };

  /**
   * Handles form submission for saving account information.
   * Validates and formats the data before sending it to the API.
   */
  const onAccountSave = async (formData) => {
    try {
      // Validate required fields using Validator class
      const missingFields = Validate.requiredFieldsForAccountInfo(
        formData.name,
        formData.address1,
        formData.city,
        formData.zip,
        formData.countryId,
        formData.phone,
        formData.paymentTerm,
        !hasPoolPartner()
      );
      
      if (missingFields.length > 0) {
        const missingFieldsList = missingFields.join(', ');
        addAlert(`Please fill in the following required fields: ${missingFieldsList}`, 'error');
        return;
      }

      const country = countryList.find(
        (country) => country.id === Number(formData.countryId)
      );
      formData.country = country.country;

      if (!isNullOrEmpty(formData.taxRegistrationNumber)) {
        formData.taxRegistrationNumber = Validate.formatTaxRegistrationNumber(formData.taxRegistrationNumber);
      }

      if (hasPoolPartner()) {
        formData.paymentTerm = "";
        formData.paymentTermName = null;
      } else if (!isNullOrEmpty(formData.paymentTerm)) {
        formData.paymentTermName = Validate.removeSpaces(
          paymentTermList
            .find((term) => term.id.toString() === formData.paymentTerm?.toString())
            ?.displayName || ""
        );
      }
      account.name = formData.name;
      account.address1 = formData.address1;
      account.address2 = formData.address2;
      account.city = formData.city;
      account.zip = formData.zip;
      account.countryId = parseInt(formData.countryId, 10);
      account.phone = formData.phone;
      account.salesEmail = formData.salesEmail;
      account.financeEmail = formData.financeEmail;
      account.salesContactName = formData.salesContactName;
      account.salesContactMobile = formData.salesContactMobile;
      account.paymentTerm = isNullOrEmpty(formData?.paymentTerm) ? null : parseInt(formData.paymentTerm, 10);
      account.paymentTermName = formData?.paymentTermName ?? null;
      account.taxRegistrationNumber = formData.taxRegistrationNumber;
      account.notes = formData.notes;
      account.isSaved = true;

      await apiUpdateRequest(account, token);
      setAccountHasChanged(false);

      navigate(`/${token}/bank`);
    } catch (error) {
      // Server returns internal field names (e.g. SalesContactMobile); show the on-form labels instead.
      const fieldLabels = {
        Name: "Account Name",
        Address1: "Address Line 1",
        Address2: "Address Line 2",
        City: "City/Town",
        Zip: "Postal Code",
        Phone: "Phone",
        SalesEmail: "Sales email address",
        FinanceEmail: "Finance email address",
        SalesContactName: "Sales contact name",
        SalesContactMobile: "Sales contact phone/mobile",
        TaxRegistrationNumber: "Tax Registration No.",
        Notes: "Notes",
      };
      const validationErrors = error?.response?.data?.errors;
      if (validationErrors) {
        const messages = Object.entries(validationErrors).flatMap(([field, fieldMessages]) => {
          const label = fieldLabels[field];
          return (fieldMessages || []).map((msg) =>
            label ? msg.replace(new RegExp(`\\b${field}\\b`, "g"), label) : msg
          );
        });
        addAlert(messages.join(" "), "error");
      } else {
        const detail = error?.response?.data?.detail || error?.response?.data?.title;
        addAlert(detail || "Error saving account information. Please try again.", "error");
      }
    }
  };

  //Uses setAccountHasChanged to indicate that the form has been modified.
  const accountFormChanged = () => {
    setAccountHasChanged(true);
  };

  /**
   * Handles changes to the tax registration number input.
   * Removes spaces and limits the length to 20 characters.
   */
  const handleOnChangeTaxRegistrationNumber = (e) => {
    e.target.value = Validate.formatTaxRegistrationNumber(e.target.value, 20);
    accountFormChanged();
  };

  // Checks if the account type includes "Pool Partner" to conditionally render fields.
  const hasPoolPartner = () =>
    accountTypes?.split(", ").includes("Pool Partner");

  
  // Handles the next button click — runs all react-hook-form validations before saving.
  const onValidationError = (fieldErrors) => {
    const messages = {
      financeEmail: "Finance email address is not valid.",
      salesEmail: "Sales email address is not valid.",
    };
    Object.keys(fieldErrors).forEach((field) => {
      const msg = messages[field] || fieldErrors[field].message || `${field} is invalid.`;
      addAlert(msg, "error");
    });
  };
  const handleNext = handleSubmit(onAccountSave, onValidationError);

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <Alerts alerts={alerts} setAlerts={setAlerts} />

          <div className="vfp-header">
            <div className="vfp-header__step">Step 1 of 4</div>
            <h4 className="vfp-header__title">Account Information</h4>
            <p className="vfp-header__sub">Enter your company and contact details. Fields marked with * are required.</p>
          </div>

          <div className="vfp-body">
            {accountLoaded && (
              <Form id="accountForm" onSubmit={handleSubmit(onAccountSave)}>
                <Row>
                  <Col sm="12" md="4">
                    <FormGroup>
                      <LabelWithToolTip id="labelAccountName" labelText="Account Name *" tooltipText="Must match bank account name. If different, explain in 'Notes' (max 100 characters)." />
                      <input
                        maxLength="100"
                        {...register("name", {
                          required: true,
                          validate: value =>
                            !/[^a-zA-Z0-9 (),.]/.test(value || "") ||
                            "Invalid characters in Account Name. Only English letters (A-Z), digits, spaces and ( ) , . are allowed."
                        })}
                        type="text"
                        className="form-control"
                      />
                    </FormGroup>
                  </Col>
                  <Col sm="12" md="4">
                    <FormGroup>
                      <LabelWithToolTip id="labelAddress1" labelText="Address Line 1 *" tooltipText="First line of company address (max 35 characters)" />
                      <input maxLength="35" {...register("address1", { required: true })} type="text" className="form-control" />
                    </FormGroup>
                  </Col>
                  <Col sm="12" md="4">
                    <FormGroup>
                      <LabelWithToolTip id="labelAddress2" labelText="Address Line 2" tooltipText="Second line of company address (max 50 characters)" />
                      <input maxLength="50" {...register("address2", {required: false})} type="text" className="form-control" />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col sm="12" md="4">
                    <FormGroup>
                      <LabelWithToolTip id="labelCity" labelText="City/Town *" tooltipText="City of registered address" />
                      <input {...register("city", { required: true })} type="text" maxLength="30" className="form-control" />
                    </FormGroup>
                  </Col>
                  <Col sm="12" md="4">
                    <FormGroup>
                      <LabelWithToolTip id="labelZip" labelText="Postal Code *" tooltipText="Postal code of registered address" />
                      <input {...register("zip", { required: true })} type="text" className="form-control" />
                    </FormGroup>
                  </Col>
                  <Col sm="12" md="4">
                    <FormGroup>
                      <LabelWithToolTip id="labelCountry" labelText="Country *" tooltipText="Country of registered address" />
                      {(() => {
                        const opts = countryList.map(c => ({ value: c.id, label: c.countryName }));
                        const currentId = watchedValues.countryId;
                        return (
                          <>
                            <input type="hidden" {...register("countryId", { required: true })} />
                            <Select
                              options={opts}
                              value={opts.find(o => String(o.value) === String(currentId)) || null}
                              onChange={(selected) => setValue("countryId", selected ? selected.value : "")}
                              placeholder="Search country…"
                              isClearable={false}
                            />
                          </>
                        );
                      })()}
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col sm="12" md="4">
                    <FormGroup>
                      <LabelWithToolTip id="labelPhone" labelText="Phone *" tooltipText="Finance department contact number" />
                      <input {...register("phone", { required: true })} type="tel" className="form-control" />
                    </FormGroup>
                  </Col>
                  <Col sm="12" md={hasPoolPartner() ? "8" : "4"}>
                    <FormGroup>
                      <LabelWithToolTip id="labelTax" labelText="Tax Registration No." tooltipText="(eg. VAT/GST No) Max 20 characters, no spaces. Leave blank if not registered" />
                      <input {...register("taxRegistrationNumber")} type="text" className="form-control" onChange={handleOnChangeTaxRegistrationNumber} />
                    </FormGroup>
                  </Col>
                  {!hasPoolPartner() && (
                    <Col sm="12" md="4">
                      <FormGroup>
                        <LabelWithToolTip id="labelPayment" labelText="Payment terms in days *" tooltipText="Select payment terms (default: 30 days)" />
                        <select {...register("paymentTerm", { required: "Payment terms is required." })} className="form-control">
                          <option value="">Select payment terms</option>
                          {paymentTermList.map((term) => (
                            <option key={term.id} value={term.id}>{term.displayName}</option>
                          ))}
                        </select>
                      </FormGroup>
                    </Col>
                  )}
                </Row>
                <Row>
                  <Col sm="12" md="4">
                    <FormGroup>
                        <LabelWithToolTip
                          id="labelFinanceEmail"
                          labelText="Finance email address"
                          tooltipText="Email address for remittance correspondence"
                        />
                        <input
                          {...register("financeEmail", {
                            validate: value =>
                              !value ||
                              /^[^\s@]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value) ||
                              "Please enter a valid email address"
                          })}
                          type="text"
                          className="form-control"
                        />

                    </FormGroup>
                  </Col>
                  <Col sm="12" md="4">
                    <FormGroup>
                        <LabelWithToolTip
                          id="labelSalesEmail"
                          labelText="Sales email address"
                          tooltipText="Email address for sales correspondence"
                        />
                        <input
                          {...register("salesEmail", {
                            validate: value =>
                              !value ||
                              /^[^\s@]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value) ||
                              "Please enter a valid email address"
                          })}
                          type="text"
                          className="form-control"
                        />

                    </FormGroup>
                  </Col>
                  <Col sm="12" md="4">
                    <FormGroup>
                      <LabelWithToolTip id="labelContactName" labelText="Sales contact name" tooltipText="Name of sales contact person" />
                      <input {...register("salesContactName")} type="text" className="form-control" />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col sm="12" md="4">
                    <FormGroup>
                      <LabelWithToolTip id="labelContactPhone" labelText="Sales contact phone/mobile" tooltipText="Phone number of sales contact person" />
                      <input {...register("salesContactMobile")} type="tel" className="form-control" />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <FormGroup>
                      <LabelWithToolTip id="labelNotes" labelText="Notes" tooltipText="Explain any difference between account name and bank account name" />
                      <textarea {...register("notes")} className="form-control" rows="3"></textarea>
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            )}

            <div className="mt-4 d-flex justify-content-end">
              <Button className="btn-next" onClick={handleNext}>
                Next: Bank Details
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
    
  );
};

export default AccountInformationPage;
